import { useMutation, useQueryClient, UseMutationResult } from '@tanstack/react-query';
import { UpdateUserData } from '../../services/authService';
import { useAppContext } from '../../contexts/Storage';
import { User } from '../../models/User';
import { apiClient } from '../../utils/api';
import { UrlBuilder } from '../../utils/urls';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserApiResponse {
  id: number;
  username: string;
  password?: string;
  email: string;
  is_google_auth: number;
  image_url?: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  last_login?: string;
  last_online?: string;
}

export interface UpdateUserApiResponse {
  message: string;
  user: UserApiResponse;
  verified?: {
    lastname?: string;
    email?: string;
    code?: number;
  };
}

export interface UpdateUserParams {
  username?: string;
  firstname?: string;
  lastname?: string;
  email?: string;
  password?: string;
  password_confirmation?: string;
  image_url?: string;
  code?: number | string;
}

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const USER_QUERY_KEY = ['user'] as const;

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

interface UseUpdateUserOptions {
  onSuccess?: (data: UpdateUserApiResponse) => void;
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for updating user profile.
 * Requires authentication token.
 */
export function useUpdateUser(
  options?: UseUpdateUserOptions
): UseMutationResult<UpdateUserApiResponse, Error, UpdateUserParams> {
  const { token, setUser, user } = useAppContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateUserParams) => {
      if (!token) {
        throw new Error('User not authenticated');
      }

      // Build payload with only defined values so backend receives a clean body (matches Postman)
      const updateData: Record<string, string | undefined> = {};
      if (params.username !== undefined) updateData.username = params.username;
      if (params.firstname !== undefined) updateData.firstname = params.firstname;
      if (params.lastname !== undefined) updateData.lastname = params.lastname;
      if (params.email !== undefined) updateData.email = params.email;
      if (params.password) updateData.password = params.password;
      if (params.password_confirmation) updateData.password_confirmation = params.password_confirmation;
      if (params.image_url !== undefined) updateData.image_url = params.image_url;
      if (params.code !== undefined && params.code !== '') updateData.code = params.code?.toString();

      const response = await apiClient.putWithToken<UpdateUserApiResponse>(
        UrlBuilder.user.update(),
        token,
        updateData
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to update user');
      }

      return response.data as UpdateUserApiResponse;
    },
    onSuccess: (data) => {
      if (data.user && setUser) {
        const updatedUser = new User({
          id: data.user.id,
          username: data.user.username,
          email: data.user.email,
          imageUrl: data.user.image_url,
          firstName: data.user.first_name,
          lastName: data.user.last_name,
        });
        setUser(updatedUser);
      }

      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

// Re-export types
export type { UpdateUserData };
